import { colorConstants } from 'config/colorConstants';
import dayjs from 'dayjs';
import _ from 'lodash';
import { fetchCurrency } from 'utils/helperMethods';

//PRICE LADDER CHART PARSER
//accepts unparsed data
//returns parsed data
export const priceLadderAttributeLevelChartParser = (data: any[], config: any, ppgName: string) => {
    const result: any = {
        product: null,
        data: { PU: [], PEQ: [] },
        lineData: { PU: [], PEQ: [] },
        minSales: 0,
        maxSales: 0,
        minPeq: Number.MAX_SAFE_INTEGER,
        minPu: Number.MAX_SAFE_INTEGER,
        maxPeq: 0,
        maxPu: 0,
        maxPrice: 0,
    };
    try {
        result.product = ppgName;
        const { priceLadder } = config;
        const kpis: any = priceLadder.dataKeyMapping;
        const subTileData = priceLadder.subTileData;
        data?.map((data: any) => {
            result.minSales = Math.min(data?.salesValueDollars, result.minSales);
            result.maxSales = Math.max(data?.salesValueDollars, result.maxSales);
            subTileData.forEach((item: any, index: number) => {
                const isTilePu = item?.key?.toLowerCase() === 'pu';
                result?.lineData?.[item?.key]?.push({
                    retailer: data?.attributeValue,
                    mraup: priceLadder.hideMraup
                        ? -1000000
                        : data?.[isTilePu ? 'perUnitValues' : 'perEqValues']['mraup'], //giving a negative value here so that tooltip appears, giving a null will make the tooltip disappear
                    category: 'Sales $',
                    price: null,
                    sales: data?.salesValueDollars,
                });
                const subData: any = [];
                kpis[item?.key]?.forEach((kpi: any, idx: number) => {
                    if (isTilePu) {
                        result.minPu = Math.min(
                            result.minPu,
                            Number(data?.['perUnitValues'][kpi?.key] || result.minPu),
                            Number(data?.['perUnitValues']['mraup'] || result.minPu),
                        );
                        result.maxPu = Math.max(
                            result.maxPu,
                            Number(data?.['perUnitValues'][kpi?.key] || 0),
                            Number(data?.['perUnitValues']['mraup'] || 0),
                        );
                    } else {
                        result.minPeq = Math.min(
                            result.minPeq,
                            Number(data?.['perEqValues'][kpi?.key] || result.minPeq),
                            Number(data?.['perEqValues']['mraup'] || result.minPeq),
                        );
                        result.maxPeq = Math.max(
                            result.maxPeq,
                            Number(data?.['perEqValues'][kpi?.key] || 0),
                            Number(data?.['perEqValues']['mraup'] || 0),
                        );
                    }
                    const tempObj: any = {};
                    tempObj['retailer'] = data?.attributeValue;
                    tempObj['sales'] = data?.salesValueDollars;
                    tempObj['salesPercentage'] = data?.percentSalesValue;
                    tempObj['category'] = kpi?.label;
                    tempObj['price'] = idx === 0 ? null : data?.[isTilePu ? 'perUnitValues' : 'perEqValues'][kpi?.key];
                    tempObj['mraup'] = data?.[isTilePu ? 'perUnitValues' : 'perEqValues']['mraup'];
                    subData.push(tempObj);
                });
                result.data?.[item?.key]?.push(subData);
            });
        });
        return result;
    } catch (error) {
        console.log(error);
        throw error;
    }
};

//table parser
//accepts unparsed data
//returns parsed data
export const priceLadderAttributeLevelTableParser = (data: any[], config: any) => {
    const result: any[] = [];
    try {
        const { priceLadder } = config;
        const tableColumnKeysMapping: any = priceLadder.tableColumnLabelKeyMapping;
        const currency = fetchCurrency(config) || '';

        const mergedData: any[] = data?.map((item: any) => {
            return {
                sales: { pre: currency, post: '', value: item?.salesValueDollars },
                sales_percentage: { pre: '', post: '%', value: item?.percentSalesValue },
                avg_price_pu: { pre: currency, post: '', value: item?.perUnitValues?.avgPrice },
                avg_price_peq: { pre: currency, post: '', value: item?.perEqValues?.avgPrice },
                max_price_pu: { pre: currency, post: '', value: item?.perUnitValues?.maxPrice },
                max_price_peq: { pre: currency, post: '', value: item?.perEqValues?.maxPrice },
                min_price_pu: { pre: currency, post: '', value: item?.perUnitValues?.minPrice },
                min_price_peq: { pre: currency, post: '', value: item?.perEqValues?.minPrice },
                mraup: { pre: currency, post: '', value: item?.perEqValues?.mraup },
                base_price_pu: { pre: currency, post: '', value: item?.perUnitValues?.basePrice },
                base_price_peq: { pre: currency, post: '', value: item?.perEqValues?.basePrice },
                promo_price_pu: { pre: currency, post: '', value: item?.perUnitValues?.promoPrice },
                promo_price_peq: { pre: currency, post: '', value: item?.perEqValues?.promoPrice },
            };
        });
        Object.keys(tableColumnKeysMapping).map((item) => {
            const tempObj: any = {
                type: tableColumnKeysMapping?.[item]?.label,
            };
            data?.map((data: any, index: number) => {
                tempObj[data?.attributeValue + ' '] = mergedData?.[index]?.[item];
            });
            result.push(tempObj);
        });
        return result;
    } catch (error) {
        throw error;
    }
};

//chart 2 parser
//accepts unparsed data
//returns parsed data
export const priceLadderAttributeLevelChart2Parser = (data: any[], baselineValue: any) => {
    const result: any = {
        weightedAvg: 0,
        minVal: 0,
        maxVal: 0,
        data: [],
    };
    try {
        result.weightedAvg = baselineValue;
        result.data = data?.map((item: any) => {
            result.minVal = Math.min(result?.minVal, item?.diffValue, baselineValue);
            result.maxVal = Math.max(result?.maxVal, item?.diffValue, baselineValue);
            return {
                sales: item?.diffValue,
                category: item?.attributeValue,
                color: item?.diffValue > 0 ? colorConstants.limeGreen : colorConstants.red,
            };
        });
        return result;
    } catch (error) {
        throw error;
    }
};

export const priceLadderDropdownParser = (data: any[], attributeType: string, dimension: string) => {
    try {
        let defaultCount: any[] = [];
        const result = data?.map((item, index) => {
            if (item?.is_default) defaultCount.push(item?.name);
            return {
                isChecked: item?.is_default,
                attributeExId: item?.name,
                attributeDesc: item?.name,
                attributeType,
                dimension,
            };
        });
        return { result, defaultCount };
    } catch (error) {
        throw error;
    }
};

export const priceLadderMultiLineParser = (data: any[], config: any) => {
    try {
        const { commonConfig } = config;
        const result: any[] = [];
        const xAxisValues: any[] = [];
        let min = Number.MAX_SAFE_INTEGER;
        let max = Number.MIN_SAFE_INTEGER;
        data?.map((item) => {
            const parsedXAxisItem = JSON.parse(item);
            const xAxisField = Object.keys(parsedXAxisItem)?.[0];
            const xAxisAttributes = Object.keys(parsedXAxisItem[xAxisField]);
            xAxisValues.push(xAxisField);
            xAxisAttributes.map((xItem) => {
                const temp: any = {};
                const value = _.isNull(parsedXAxisItem[xAxisField][xItem])
                    ? parsedXAxisItem[xAxisField][xItem]
                    : _.round(parsedXAxisItem[xAxisField][xItem], 2);
                min = Math.min(value || Number.MAX_SAFE_INTEGER, min);
                max = Math.max(value || Number.MIN_SAFE_INTEGER, max);
                temp['xAxisData'] = dayjs(xAxisField).format(commonConfig?.dateFormat);
                temp['value'] = value;
                temp['type'] = xItem;
                result.push(temp);
            });
        });
        return { result, min, max, xAxisValues };
    } catch (error) {
        throw error;
    }
};
